import { TYPE, parse, type MessageFormatElement } from "@formatjs/icu-messageformat-parser";

/*
 * ICU prints a plain argument such as `{count}` with String(), so a number shows Latin digits
 * even in Arabic; only `#` and `{count, number}` go through the locale's number format. Two
 * steps put every count in the reader's digits without touching the catalogs:
 *
 * 1. When the catalogs load, a plain argument that a message also uses to choose a plural or a
 *    select branch is known to be a number, so it becomes `{name, number}`.
 * 2. When a message is formatted (useT), a number given for any other plain argument is
 *    formatted for the locale first. Arguments ICU formats itself (choices, `{x, number}`,
 *    dates) are left alone.
 */

type Elements = MessageFormatElement[];

/** Names of the arguments that choose a plural, selectordinal or select branch. */
function choiceArguments(elements: Elements, names = new Set<string>()): Set<string> {
  for (const element of elements) {
    if (element.type === TYPE.plural || element.type === TYPE.select) {
      names.add(element.value);
    }
    if ("options" in element) {
      for (const option of Object.values(element.options)) choiceArguments(option.value, names);
    }
    if (element.type === TYPE.tag) choiceArguments(element.children, names);
  }
  return names;
}

/** Turns every plain use of a choice argument into a number element, all the way down. */
function numberChoiceArguments(elements: Elements, names: ReadonlySet<string>): Elements {
  return elements.map((element) => {
    if (element.type === TYPE.argument && names.has(element.value)) {
      return { type: TYPE.number, value: element.value, location: element.location, style: null };
    }
    if (element.type === TYPE.plural || element.type === TYPE.select) {
      const options = Object.fromEntries(
        Object.entries(element.options).map(([key, option]) => [
          key,
          { ...option, value: numberChoiceArguments(option.value, names) },
        ]),
      );
      return { ...element, options };
    }
    if (element.type === TYPE.tag) {
      return { ...element, children: numberChoiceArguments(element.children, names) };
    }
    return element;
  });
}

/** The parsed form of one message, with its choice arguments as number elements. */
export function compileMessage(message: string): Elements {
  const elements = parse(message);
  return numberChoiceArguments(elements, choiceArguments(elements));
}

/** Every message of a catalog, parsed once (react-intl takes the parsed form as it is). */
export function compileMessages<Id extends string>(
  messages: Readonly<Record<Id, string>>,
): Record<Id, Elements> {
  const compiled = {} as Record<Id, Elements>;
  for (const id of Object.keys(messages) as Id[]) compiled[id] = compileMessage(messages[id]);
  return compiled;
}

/** Names of the arguments ICU formats itself: choices, and explicit numbers, dates and times. */
function typedArguments(elements: Elements, names = new Set<string>()): Set<string> {
  for (const element of elements) {
    if (
      element.type === TYPE.plural ||
      element.type === TYPE.select ||
      element.type === TYPE.number ||
      element.type === TYPE.date ||
      element.type === TYPE.time
    ) {
      names.add(element.value);
    }
    if ("options" in element) {
      for (const option of Object.values(element.options)) typedArguments(option.value, names);
    }
    if (element.type === TYPE.tag) typedArguments(element.children, names);
  }
  return names;
}

const TYPED = new WeakMap<Elements, Set<string>>();

/**
 * The values to format a message with: numbers for its plain arguments become strings in the
 * locale's digits; numbers ICU formats itself (choices, `{x, number}`) are left as numbers.
 */
export function localizeNumberValues<Value>(
  message: Elements | string | undefined,
  values: Record<string, Value | number> | undefined,
  formatNumber: (value: number) => string,
): Record<string, Value | number | string> | undefined {
  if (!values || !Array.isArray(message)) return values;
  let typed = TYPED.get(message);
  if (!typed) {
    typed = typedArguments(message);
    TYPED.set(message, typed);
  }
  const known = typed;
  return Object.fromEntries(
    Object.entries(values).map(([name, value]) => [
      name,
      typeof value === "number" && !known.has(name) ? formatNumber(value) : value,
    ]),
  );
}
